import { useQuery } from '@tanstack/react-query'
import { Gauge, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { billing } from '@/api/endpoints'
import { cn, formatDate } from '@/lib/utils'

import type { PlanUsage } from './types'

function useUsage() {
  const { data } = useQuery({
    queryKey: ['billing-status'],
    queryFn: billing.status,
    staleTime: 30 * 60 * 1000,
  })
  return data?.usage ?? null
}

const LEVEL_STYLE: Record<PlanUsage['level'], string> = {
  ok: 'bg-emerald-500',
  near: 'bg-amber-500',
  over: 'bg-red-600',
}

/**
 * Využitie balíčka v Dokumentoch - bežný limit, limit v špičke a koľko
 * špičkových dní sa už v sezóne minulo (`contracts.usage`). Nič sa
 * neblokuje; je to informácia, aby škola nebola prekvapená.
 */
export function PlanUsageSection() {
  const { t } = useTranslation()
  const usage = useUsage()
  if (!usage) return null

  const share = Math.min(100, (usage.peak_days / usage.peak_days_allowed) * 100)
  return (
    <section className="space-y-3">
      <header>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Gauge size={15} className="text-slate-400" />
          {t('billing.usage.title', { plan: usage.plan_name, season: usage.season })}
        </h2>
        <p className="text-xs text-slate-500">
          {usage.peak_limit
            ? t('billing.usage.rule', {
                limit: usage.limit,
                peakLimit: usage.peak_limit,
                days: usage.peak_days_allowed,
              })
            : t('billing.usage.ruleNoCap', { limit: usage.limit, days: usage.peak_days_allowed })}
        </p>
        <p className="text-xs text-slate-500">{t('billing.usage.counted')}</p>
      </header>

      <div className="space-y-2 rounded-md border border-slate-200 p-3">
        <div className="flex items-baseline justify-between text-sm">
          <span>{t('billing.usage.peakDays')}</span>
          <span className="font-semibold tabular-nums">
            {usage.peak_days} / {usage.peak_days_allowed}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className={cn('h-full', LEVEL_STYLE[usage.level])} style={{ width: `${share}%` }} />
        </div>
        <p className="text-xs text-slate-500">
          {t('billing.usage.maxPerDay', { count: usage.max_per_day })}
          {usage.recent_peak_days.length > 0
            ? ` · ${t('billing.usage.lastPeak', {
                date: formatDate(usage.recent_peak_days[usage.recent_peak_days.length - 1]),
              })}`
            : ''}
        </p>
        {usage.level !== 'ok' ? (
          <p className={cn('text-sm', usage.level === 'over' ? 'text-red-700' : 'text-amber-700')}>
            {usage.level === 'over'
              ? usage.over_cap_days > 0
                ? t('billing.usage.overCap', { count: usage.over_cap_days, peakLimit: usage.peak_limit })
                : t('billing.usage.overDays', { days: usage.peak_days_allowed })
              : t('billing.usage.near', { left: usage.peak_days_allowed - usage.peak_days })}
          </p>
        ) : null}
        <p className="flex items-start gap-1.5 text-xs text-slate-500">
          <Info size={13} className="mt-0.5 shrink-0" />
          {t('billing.usage.neverBlocked')}
        </p>
      </div>
    </section>
  )
}

/** Pás pod hlavičkou pre správcu, keď sa škola blíži k limitu balíčka alebo
 *  ho prekročila. Ostatní ho nevidia - nemôžu s tým nič urobiť. */
export function PlanUsageBanner({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation()
  const usage = useUsage()
  if (!isAdmin || !usage || usage.level === 'ok') return null

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 text-sm',
        usage.level === 'over' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900',
      )}
    >
      <Gauge size={15} className="shrink-0" />
      <span className="flex-1">
        {usage.level === 'over'
          ? t('billing.usage.bannerOver', { plan: usage.plan_name })
          : t('billing.usage.bannerNear', {
              used: usage.peak_days,
              days: usage.peak_days_allowed,
            })}
      </span>
      <Link to="/settings/subscription" className="font-semibold underline underline-offset-2">
        {t('billing.usage.details')}
      </Link>
    </div>
  )
}
